import TopNav from './components/TopNav.jsx'
import Header from './components/Header.jsx'
import PillarTabs from './components/PillarTabs.jsx'
import PresenceBar from './components/PresenceBar.jsx'
import CommentPanel from './components/CommentPanel.jsx'
import MentionToast from './components/MentionToast.jsx'
import UndoToast from './components/UndoToast.jsx'
import DmPanel from './components/DmPanel.jsx'
import DmToast from './components/DmToast.jsx'
import ExecutiveSummaryCard from './components/ExecutiveSummaryCard.jsx'
import WeeklyArchivePanel from './components/WeeklyArchivePanel.jsx'
import GuidedTour from './components/GuidedTour.jsx'
import PillarPlaceholder from './pillars/PillarPlaceholder.jsx'
import RevenuePillar from './pillars/RevenuePillar.jsx'
import EoBuyoutsPillar from './pillars/EoBuyoutsPillar.jsx'
import GrowthPillar from './pillars/GrowthPillar.jsx'
import DeliveryPillar from './pillars/DeliveryPillar.jsx'
import TeamPillar from './pillars/TeamPillar.jsx'
import AiSystemsPillar from './pillars/AiSystemsPillar.jsx'
import { useAuth } from './hooks/useAuth.js'
import { usePresenceHeartbeat } from './hooks/usePresenceHeartbeat.js'
import { useRealtimeSync } from './hooks/useRealtimeSync.js'
import { useUndo } from './hooks/useUndo.js'
import { useAutoSnapshot } from './hooks/useAutoSnapshot.js'
import { useEffect } from 'react'
import { useTourStore } from './store/tourStore.js'
import { useUIStore } from './store/uiStore.js'
import { PILLARS } from './data/pillars.js'

/**
 * Root app.
 *
 * The active pillar is driven by a Zustand store (useUIStore), so anything
 * in the tree — tabs, keyboard shortcuts, programmatic navigation —
 * can change which pillar is shown without prop-drilling.
 *
 * Future pillar components will be registered in a pillar router below
 * (see the switch statement). For now, every pillar renders the same
 * placeholder.
 */
export default function App() {
  const activePillarIndex = useUIStore((s) => s.activePillar)
  const activePillar = PILLARS[activePillarIndex]

  // Auth: auto-detect user from /whoami. No-op for components.
  useAuth()

  // Presence: write our heartbeat every 20s so others see we're here.
  usePresenceHeartbeat()

  // Real-time: subscribe to Ably, invalidate caches on remote changes.
  // No-ops if ABLY_API_KEY isn't set on the worker — falls back to polling.
  useRealtimeSync()

  // Undo: bind ⌘Z / Ctrl+Z keyboard shortcut app-wide
  useUndo()

  // Weekly snapshot: archive prior-week state on the first Monday visit.
  // Runs once per session, no-op if already snapshotted.
  useAutoSnapshot()

  // Auto-launch the guided tour on the user's first visit. Anyone who
  // has previously completed or skipped the tour won't see it again.
  // They can replay it any time via the 🧭 Tour button in the top nav.
  const tourCompleted = useTourStore((s) => s.completed)
  const tourOpen = useTourStore((s) => s.isOpen)
  const startTour = useTourStore((s) => s.start)
  useEffect(() => {
    if (!tourCompleted && !tourOpen) {
      // Small delay so the page has a chance to render first; the tour
      // measures DOM elements, so it needs them mounted.
      const t = setTimeout(() => startTour(), 600)
      return () => clearTimeout(t)
    }
  }, [tourCompleted, tourOpen, startTour])

  return (
    <>
      <TopNav />
      <main className="page">
        <Header />
        <ExecutiveSummaryCard />
        <PillarTabs />
        <PresenceBar />
        <PillarContent pillarId={activePillar.id} />
      </main>
      <CommentPanel />
      <MentionToast />
      <UndoToast />
      <DmPanel />
      <DmToast />
      <WeeklyArchivePanel />
      <GuidedTour />
    </>
  )
}

/**
 * PillarContent — decides which pillar component to render.
 *
 * Today every pillar returns the placeholder. As we port them in future
 * sessions, add cases here:
 *   case 'revenue':
 *     return <RevenuePillar />
 */
function PillarContent({ pillarId }) {
  switch (pillarId) {
    case 'revenue':
      return <RevenuePillar />
    case 'eo-buyouts':
      return <EoBuyoutsPillar />
    case 'growth':
      return <GrowthPillar />
    case 'delivery':
      return <DeliveryPillar />
    case 'team':
      return <TeamPillar />
    case 'ai':
      return <AiSystemsPillar />
    default:
      return <PillarPlaceholder pillarId={pillarId} />
  }
}
